const PX_PER_MM = 96 / 25.4; // CSS reference px per mm
const PAD_TOP_MM = 20;
const PAD_BOTTOM_MM = 10;
const PAD_X_MM = 8;
const CARD_SAFETY_MM = 4;

const TABLE_SEL = "table.report, table.ledger";
const BLOCK_SEL = ".unit-title, .machine-title, .stage-gap, .ledger-note, .ledger-separator";
const KEEP_SEL = ".unit-title, .machine-title";

function dims(orientation) {
  const portrait = orientation === "portrait";
  const pageW = portrait ? 210 : 297;
  const pageH = portrait ? 297 : 210;
  return {
    contentWmm: pageW - 2 * PAD_X_MM,
    contentPx: (pageH - PAD_TOP_MM - PAD_BOTTOM_MM - CARD_SAFETY_MM) * PX_PER_MM,
  };
}

function extractHeader(titleEl) {
  let ym = "";
  let title = "";
  let metaLines = [];
  if (titleEl) {
    const period = titleEl.querySelector(".period");
    if (period) ym = period.textContent.trim();
    const h1 = titleEl.querySelector("h1");
    if (h1) title = h1.textContent.trim();
    const meta = titleEl.querySelector(".meta");
    if (meta) {
      metaLines = meta.innerHTML
        .split(/<br\s*\/?>/i)
        .map((s) => s.replace(/<[^>]*>/g, "").trim())
        .filter(Boolean);
    }
  }
  return { ym, title, metaLines };
}

function collectItems(node, items) {
  Array.from(node.children).forEach((child) => {
    if (child.matches(".report-title")) {
      return; // values are lifted into the running header, not flowed
    } else if (child.matches(TABLE_SEL)) {
      items.push({ type: "table", el: child });
    } else if (child.matches(BLOCK_SEL)) {
      items.push({ type: "block", el: child, keepWithNext: child.matches(KEEP_SEL) });
    } else {
      collectItems(child, items);
    }
  });
}

function tableUnits(tbody) {
  const rows = Array.from(tbody.children).filter((n) => n.tagName === "TR");
  const units = [];
  let i = 0;
  while (i < rows.length) {
    let span = 1;
    for (const cell of rows[i].children) {
      if (cell.rowSpan > span) span = cell.rowSpan;
    }
    units.push(rows.slice(i, i + span));
    i += span;
  }
  return units;
}

function verticalMargin(el) {
  const cs = getComputedStyle(el);
  return (parseFloat(cs.marginTop) || 0) + (parseFloat(cs.marginBottom) || 0);
}

function newTableShell(item) {
  const table = document.createElement("table");
  table.className = item.el.className;
  const style = item.el.getAttribute("style");
  if (style) table.setAttribute("style", style);
  if (item.colgroup) table.appendChild(item.colgroup.cloneNode(true));
  if (item.thead) table.appendChild(item.thead.cloneNode(true));
  table.appendChild(document.createElement("tbody"));
  return table;
}

function addHeadersFooters(cards, header) {
  cards.forEach((card, index) => {
    const head = document.createElement("div");
    head.className = "tool-report-header";

    const meta = document.createElement("div");
    meta.className = "tr-meta";
    header.metaLines.forEach((line) => {
      const row = document.createElement("div");
      row.textContent = line;
      meta.appendChild(row);
    });
    head.appendChild(meta);

    const title = document.createElement("div");
    title.className = "tr-title";
    title.textContent = header.title || "";
    head.appendChild(title);

    const ym = document.createElement("div");
    ym.className = "tr-ym";
    ym.textContent = header.ym || "";
    head.appendChild(ym);
    card.appendChild(head);

    const foot = document.createElement("div");
    foot.className = "tool-report-footer";
    foot.textContent = `ページ ${index + 1}`;
    card.appendChild(foot);
  });
}

// Re-flow one report (a single tab) into a stack of A4 cards. The source markup
// is preserved on `report._sourceHTML` so this can run again on every
// orientation / tab / data change.
function paginate(report, orientation) {
  const source = report._sourceHTML;
  if (source == null) return;

  try {
    const { contentWmm, contentPx: avail } = dims(orientation);

    report.innerHTML = "";
    const pages = document.createElement("div");
    pages.className = "tool-report-pages";
    report.appendChild(pages);

    const measure = document.createElement("div");
    measure.className = "tool-report-measure";
    measure.style.width = `${contentWmm}mm`;
    measure.innerHTML = source;
    report.appendChild(measure);

    const header = extractHeader(measure.querySelector(".report-title"));

    const items = [];
    collectItems(measure, items);

    items.forEach((it) => {
      if (it.type === "block") {
        it.height = it.el.getBoundingClientRect().height + verticalMargin(it.el);
      } else {
        it.colgroup = it.el.querySelector("colgroup");
        it.thead = it.el.querySelector("thead");
        it.theadH = it.thead ? it.thead.getBoundingClientRect().height : 0;
        const cs = getComputedStyle(it.el);
        it.marginTop = parseFloat(cs.marginTop) || 0;
        it.marginBottom = parseFloat(cs.marginBottom) || 0;
        const tbody = it.el.querySelector("tbody");
        it.units = tbody ? tableUnits(tbody) : [];
        it.units.forEach((u) => {
          u.height = u.reduce((h, r) => h + r.getBoundingClientRect().height, 0);
        });
      }
    });

    const cards = [];
    let card = null;
    let body = null;
    let used = 0;
    const open = () => {
      card = document.createElement("div");
      card.className = `tool-page ${orientation}`;
      const inner = document.createElement("div");
      inner.className = "tool-page-body";
      card.appendChild(inner);
      pages.appendChild(card);
      cards.push(card);
      body = inner;
      used = 0;
    };
    open();

    for (let k = 0; k < items.length; k++) {
      const it = items[k];

      if (it.type === "block") {
        let need = it.height;
        if (it.keepWithNext && items[k + 1]) {
          const nx = items[k + 1];
          if (nx.type === "block") need += nx.height;
          else if (nx.type === "table") {
            need += nx.marginTop + nx.theadH + (nx.units[0] ? nx.units[0].height : 0);
          }
        }
        if (used > 0 && used + need > avail) open();
        body.appendChild(it.el);
        used += it.height;
        continue;
      }

      let tb = null;
      const firstH = it.units[0] ? it.units[0].height : 0;
      if (used > 0 && used + it.marginTop + it.theadH + firstH > avail) open();
      used += it.marginTop;
      const openTable = () => {
        const shell = newTableShell(it);
        body.appendChild(shell);
        used += it.theadH;
        tb = shell.querySelector("tbody");
      };
      openTable();
      for (const u of it.units) {
        if (used + u.height > avail && tb.children.length > 0) {
          open();
          openTable();
        }
        u.forEach((r) => tb.appendChild(r));
        used += u.height;
      }
      used += it.marginBottom;
    }

    measure.remove();
    addHeadersFooters(cards, header);
  } catch (err) {
    console.error("Tool report pagination failed; showing un-paginated report.", err);
    report.innerHTML = source;
  }
}

export function paginatePreview(frame, orientation) {
  if (!frame) return;

  frame.querySelectorAll(".report").forEach((r) => {
    if (r._sourceHTML == null) r._sourceHTML = r.innerHTML;
  });

  const active = frame.querySelector(".report.active");
  if (active) paginate(active, orientation);
}
